/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LogService, RbacService, UserService, UtilsService } from '../../../../../core/services';
import { Router } from '@angular/router';
import { SupportApiService } from '../../../shared/http';
import { SupportTicketModel } from '../../../shared/models';
import { environment } from '../../../../../../environments/environment';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ticket-list',
    templateUrl: './ticket-list.component.html',
    styleUrls: ['./ticket-list.component.scss']
})

export class TicketListComponent extends BaseComponent {
    /**
     * Table row data
     */
    public tableRows: SupportTicketModel[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url
     */
    public searchTerm: string = '';

    /**
     * Store the user's ability to edit
     */
    public userCanEdit: boolean = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Flag on loading state.
     */
    public isLoading: boolean = true;

    constructor(
        private logService: LogService,
        private rbacService: RbacService,
        private router: Router,
        private supportApiService: SupportApiService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpPageData();
        this.setUpUserValues();
        this.registerButtons();
    }

    /**
     * Update ticket by ID.
     * @param id
     */
    public updateTicket(id: string): void {
        this.router.navigate(['cm/support/conversation/' + id]).then().catch();
    }

    /**
     * Delete ticket by ID (non production only).
     * @param id
     */
    public deleteTicket(id: string): void {
        if (!environment.production) {
            this.supportApiService.deleteTicket(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('TicketListComponent:closeTicket', 'Error closing ticket' + error);
                    }
                });
        }
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.supportApiService.getTicketList()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.populateTableRows(data.tickets);
                    this.isLoading = false;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('TicketListComponent:setUpPageData', 'Error getting page data' + error);
                },
                complete: (): void => {
                    this.isLoading = false;
                }
            });
    }

    /**
     * Populate table rows.
     * @param tickets
     * @private
     */
    private populateTableRows(tickets: SupportTicketModel[]): void {
        this.tableRows = [];
        if (tickets?.length) {
            for (const ticket of tickets) {
                this.tableRows.push({
                    id: ticket.id,
                    subject: ticket.subject,
                    status: ticket.status,
                    product: this.getProductFromTicket(ticket),
                    priority: ticket.priority,
                    date: this.utilsService.createDateTimeString(ticket.created_at),
                    requester: ticket.requester?.name
                });
            }
        }
    }

    /**
     * Get product from ticket.
     * @param ticket
     * @private
     */
    private getProductFromTicket(ticket: SupportTicketModel): string {
        const product: string = this.utilsService.getCustomFieldValue(ticket?.custom_fields, 'Product');

        return product ? product.substring(8) : '';
    }

    /**
     * Set up user values.
     * @private
     */
    private setUpUserValues(): void {
        const user = this.userService.getUser();
        this.userCanEdit = user.permissions && (user.permissions < 4 && user.permissions !== 0);
    }

    /**
     * Register the button permissions for the page.
     * @private
     */
    private registerButtons(): void {
        const buttonReference: any = {
            'ticket-list-create-ticket': [
                { path: '/api/v1/support/tickets/', methods: ['POST'] },
            ],
            'ticket-list-update-ticket': [
                { path: '/api/v1/support/tickets/:id', methods: ['PUT'] },
                { path: '/api/v1/support/tickets/:id/comments/', methods: ['GET'] },
            ],
            'ticket-list-delete-ticket': [
                { path: '/api/v1/support/tickets/:id', methods: ['DELETE'] },
            ],
            'ticket-list-close-ticket': [
                { path: '/api/v1/support/tickets/:id', methods: ['PUT'] },
            ],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
